"use client";
import React, { Fragment, useState } from "react";
import { Popover, Transition } from "@headlessui/react";
import { usePathname } from "next/navigation";
import classNames from "classnames";
import { ChevronDownIcon } from "@heroicons/react/24/solid";

import {
  Enum_Menuitem_Variation,
  MenuItemEntity,
} from "../../../graphql/generated/graphql";

import { ComponentRenderer } from "../../ComponentRenderer";
import { TextLink } from "../../navigation/TextLink";
import { Container } from "../../layout/Container";
import { notEmpty } from "../../../utils/notEmpty";
import { getColors } from "../../../utils/colors";
import { useUI } from "../../../context";
import { getClientSiteConfig } from "../../../clientConfig";

interface IMainNavigationItem {
  item: MenuItemEntity;
}

function hasComponentActiveLink(components, asPath): boolean {
  const componentsWithLinks = components.filter(
    (c) => c.__typename === "ComponentNavigationLinkList"
  );
  let hasActiveLink = false;
  componentsWithLinks.forEach((c) => {
    switch (c.__typename) {
      case "ComponentNavigationLinkList":
        c.links?.forEach((link) => {
          if (link?.page?.data?.attributes?.path === asPath) {
            hasActiveLink = true;
          }
        });
        break;
      default:
        hasActiveLink = false;
    }
  });

  return hasActiveLink;
}

export function NavbarItem({ item }: IMainNavigationItem): JSX.Element {
  const [isShowing, setIsShowing] = useState(false);
  const asPath = usePathname();

  const siteConfig = getClientSiteConfig();

  if (!item?.attributes) {
    return <></>;
  }

  const attributes = item?.attributes;
  const variation = attributes?.variation || Enum_Menuitem_Variation.FullWidth;

  const { backgroundColorClassName, textColorClassName } = getColors(
    item?.attributes?.colors
  );

  if (
    !item.attributes?.components ||
    item.attributes?.components.length === 0
  ) {
    return (
      <button
        className={classNames({
          "font-bold border-b-4 border-b-primary-400":
            item.attributes?.page?.data?.attributes?.path === asPath,
        })}
      >
        <TextLink
          className={`${
            textColorClassName || ""
          } py-1 hover:text-gray-400 text-base prose`}
          href={
            item.attributes?.page?.data?.attributes?.path ||
            item.attributes?.externalUrl ||
            "#"
          }
        >
          {item.attributes?.label}
        </TextLink>
      </button>
    );
  }

  return (
    <Popover
      className={classNames({
        relative: variation === Enum_Menuitem_Variation.Dropdown,
      })}
    >
      {(open) => (
        <>
          <Popover.Button
            className={classNames({
              "flex items-center pb-4 -mb-4": true,
            })}
            onMouseEnter={() => setIsShowing(true)}
            onMouseLeave={() => setIsShowing(false)}
          >
            <TextLink
              href={
                item.attributes?.page?.data?.attributes?.path ||
                item.attributes?.externalUrl ||
                ""
              }
              target={
                item.attributes?.linkTarget?.data?.attributes?.value || "_self"
              }
            >
              <span>{item.attributes?.label}</span>
            </TextLink>
            <ChevronDownIcon
              className={classNames(
                open ? "text-gray-600" : "text-gray-400",
                "ml-1 h-5 w-5 group-hover:text-gray-500"
              )}
              aria-hidden="true"
            />
          </Popover.Button>

          <Transition
            show={isShowing}
            as={Fragment}
            appear={false}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel
              onMouseEnter={() => setIsShowing(true)}
              onMouseLeave={() => setIsShowing(false)}
              className={classNames({
                "absolute mt-4 inset-x-0 z-10 transform":
                  variation === Enum_Menuitem_Variation.FullWidth,
                "absolute left-1/2 z-10 mt-4 flex w-screen max-w-md -translate-x-1/2 px-4":
                  variation === Enum_Menuitem_Variation.Dropdown,
              })}
            >
              {/* Presentational element used to render the bottom shadow, if we put the shadow on the actual panel it pokes out the top, so we use this shorter element to hide the top of the shadow */}

              <div
                className={classNames({
                  "inset-x-0 text-sm shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden":
                    variation === Enum_Menuitem_Variation.FullWidth,
                  "max-w-md flex-auto overflow-hidden rounded-xl bg-white text-sm leading-6 shadow-lg ring-1 ring-gray-900/5":
                    variation === Enum_Menuitem_Variation.Dropdown,
                  [`${backgroundColorClassName}`]: true,
                  ["bg-white"]: !!!Boolean(backgroundColorClassName),
                })}
              >
                {variation === Enum_Menuitem_Variation.FullWidth && (
                  <Container>
                    <div className="relative flex justify-center gap-4 md:gap-8 lg:gap-20">
                      <ComponentRenderer
                        components={
                          item.attributes?.components?.filter(notEmpty) || []
                        }
                      />
                    </div>
                  </Container>
                )}
                {variation === Enum_Menuitem_Variation.Dropdown && (
                  <div className="p-4">
                    <ComponentRenderer
                      components={
                        item.attributes?.components?.filter(notEmpty) || []
                      }
                      componentConfiguration={{
                        ComponentNavigationLinkList: {
                          context: "DropdownNavbarItem",
                        },
                      }}
                    />
                  </div>
                )}
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}
